import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Plus, Pencil, Trash2, Search, Users, Check, Shield, UserCog, Briefcase, Building2, Package, Calculator, ArrowUpDown, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '../../components/ui/alert-dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../../components/ui/tabs';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { Checkbox } from '../../components/ui/checkbox';
import { toast } from 'sonner';
import { ScrollArea } from '../../components/ui/scroll-area';
import { Badge } from '../../components/ui/badge';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Available panels - add new panels here
const AVAILABLE_PANELS = [
  { id: 'admin', name: 'Admin Paneli', icon: Shield, color: 'bg-red-100 text-red-700' },
  { id: 'hr', name: 'İK Paneli', icon: UserCog, color: 'bg-teal-100 text-teal-700' },
  { id: 'work', name: 'İş Paneli', icon: Briefcase, color: 'bg-blue-100 text-blue-700' },
  { id: 'branch', name: 'Şube Yönetimi', icon: Briefcase, color: 'bg-amber-100 text-amber-700' },
  { id: 'board', name: 'Yönetim Kurulu', icon: Building2, color: 'bg-purple-100 text-purple-700' },
  { id: 'depo', name: 'Depo Paneli', icon: Package, color: 'bg-emerald-100 text-emerald-700' },
  { id: 'purchasing', name: 'Satın Alma Paneli', icon: Package, color: 'bg-orange-100 text-orange-700' },
  { id: 'accounting', name: 'Muhasebe Paneli', icon: Calculator, color: 'bg-indigo-100 text-indigo-700' },
  // Add more panels here as needed
];

const UserManagement = () => {
  const [users, setUsers] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [professions, setProfessions] = useState([]);
  const [positions, setPositions] = useState([]);
  const [branches, setBranches] = useState([]);
  const [isOpen, setIsOpen] = useState(false);
  const [loading, setLoading] = useState(false);
  const [editingUser, setEditingUser] = useState(null);
  const [employeeSearch, setEmployeeSearch] = useState('');
  const [selectedEmployee, setSelectedEmployee] = useState(null);
  const [employeesLoading, setEmployeesLoading] = useState(false);
  const [selectedPanels, setSelectedPanels] = useState([]);
  
  // Sıralama state'leri
  const [sortField, setSortField] = useState(null);
  const [sortDirection, setSortDirection] = useState('asc');
  
  const [formData, setFormData] = useState({
    name: '',
    password: '',
    profession_id: '',
    position_id: '',
    branch_id: '',
    employee_id: ''
  });

  // Sıralama fonksiyonu
  const handleSort = (field) => {
    if (sortField === field) {
      // Aynı alana tıklanırsa yön değiştir
      if (sortDirection === 'asc') {
        setSortDirection('desc');
      } else if (sortDirection === 'desc') {
        // Sıralamayı kaldır
        setSortField(null);
        setSortDirection('asc');
      }
    } else {
      // Yeni alan seçildi
      setSortField(field);
      setSortDirection('asc');
    }
  };

  // Sıralanmış kullanıcılar
  const sortedUsers = useMemo(() => {
    if (!sortField) return users;
    
    return [...users].sort((a, b) => {
      let aValue, bValue;
      
      switch (sortField) {
        case 'profession':
          aValue = a.profession_name || '';
          bValue = b.profession_name || '';
          break;
        case 'position':
          aValue = a.position_name || '';
          bValue = b.position_name || '';
          break;
        case 'branch':
          aValue = a.branch_name || '';
          bValue = b.branch_name || '';
          break;
        case 'panel_access':
          aValue = (a.panel_access || []).length;
          bValue = (b.panel_access || []).length;
          break;
        default:
          return 0;
      }
      
      if (typeof aValue === 'number') {
        return sortDirection === 'asc' ? aValue - bValue : bValue - aValue;
      }
      
      const comparison = aValue.localeCompare(bValue, 'tr');
      return sortDirection === 'asc' ? comparison : -comparison;
    });
  }, [users, sortField, sortDirection]);

  // Sıralama ikonu
  const SortIcon = ({ field }) => {
    if (sortField !== field) {
      return <ArrowUpDown className="h-4 w-4 ml-1 opacity-50" />;
    }
    return sortDirection === 'asc' 
      ? <ArrowUp className="h-4 w-4 ml-1 text-blue-600" />
      : <ArrowDown className="h-4 w-4 ml-1 text-blue-600" />;
  };

  useEffect(() => {
    loadUsers();
    loadProfessions();
    loadPositions();
    loadBranches();
  }, []);

  // Load employees when dialog opens
  useEffect(() => {
    if (isOpen && employees.length === 0) {
      loadEmployees();
    }
  }, [isOpen]);

  const loadUsers = async () => {
    try {
      const res = await axios.get(`${API}/admin/users`);
      setUsers(res.data);
    } catch (error) {
      console.error('Error loading users:', error);
    }
  };

  const loadEmployees = async () => {
    setEmployeesLoading(true);
    try {
      const res = await axios.get(`${API}/hr/employees`);
      setEmployees(res.data || []);
    } catch (error) {
      console.error('Error loading employees:', error);
      toast.error('Çalışanlar yüklenemedi');
    }
    setEmployeesLoading(false);
  };

  const loadProfessions = async () => {
    try {
      const res = await axios.get(`${API}/admin/professions`);
      setProfessions(res.data);
    } catch (error) {
      console.error('Error loading professions:', error);
    }
  };

  const loadPositions = async () => {
    try {
      const res = await axios.get(`${API}/admin/positions`);
      setPositions(res.data);
    } catch (error) {
      console.error('Error loading positions:', error);
    }
  };

  const loadBranches = async () => {
    try {
      const res = await axios.get(`${API}/admin/branches`);
      setBranches(res.data);
    } catch (error) {
      console.error('Error loading branches:', error);
    }
  };

  // İK kaydı bazen id, bazen serbest metin (isim) saklıyor.
  // Önce id ile, eşleşmezse isimle (büyük/küçük harf duyarsız) eşleştir.
  const resolveId = (list, id, name) => {
    if (id && list.some(x => x.id === id)) return id;
    if (name) {
      const norm = String(name).toLocaleLowerCase('tr').trim();
      const match = list.find(x => (x.name || '').toLocaleLowerCase('tr').trim() === norm);
      if (match) return match.id;
    }
    return '';
  };

  const handleSelectEmployee = (employee) => {
    setSelectedEmployee(employee);
    // İK panelindeki kişi bilgilerinden meslek/görev/şube otomatik çekilir
    setFormData({
      name: employee.name || '',
      password: '',
      profession_id: resolveId(professions, employee.profession_id, employee.profession || employee.profession_name),
      position_id: resolveId(positions, employee.position_id, employee.position || employee.position_name),
      branch_id: resolveId(branches, employee.branch_id, employee.branch || employee.branch_name),
      employee_id: employee.id
    });
  };

  const handlePanelToggle = (panelId) => {
    setSelectedPanels(prev => {
      if (prev.includes(panelId)) {
        return prev.filter(p => p !== panelId);
      } else {
        return [...prev, panelId];
      }
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // For editing, only name is required. For new users, all fields are required.
    if (!formData.name) {
      toast.error('Ad Soyad alanı zorunludur');
      return;
    }
    
    // For new users, require all fields
    if (!editingUser && (!formData.profession_id || !formData.position_id || !formData.branch_id)) {
      toast.error('Tüm alanları doldurun');
      return;
    }

    if (!editingUser && !formData.password) {
      toast.error('Şifre gerekli');
      return;
    }

    setLoading(true);
    try {
      const requestData = {
        ...formData,
        panel_access: selectedPanels
      };

      if (editingUser) {
        await axios.put(`${API}/admin/users/${editingUser.id}`, requestData);
        toast.success('Kullanıcı güncellendi');
      } else {
        await axios.post(`${API}/admin/users`, requestData);
        toast.success('Kullanıcı eklendi');
      }
      setIsOpen(false);
      setEditingUser(null);
      setSelectedEmployee(null);
      setSelectedPanels([]);
      setFormData({ name: '', password: '', profession_id: '', position_id: '', branch_id: '', employee_id: '' });
      loadUsers();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : (Array.isArray(error.response?.data?.detail) ? error.response.data.detail.map(e => e.msg).join(', ') : 'Hata oluştu'));
    }
    setLoading(false);
  };

  const handleEdit = (user) => {
    setEditingUser(user);
    setFormData({
      name: user.name,
      password: '',
      profession_id: user.profession_id || '',
      position_id: user.position_id || '',
      branch_id: user.branch_id || '',
      employee_id: user.employee_id || ''
    });
    setSelectedPanels(user.panel_access || []);
    setIsOpen(true);
  };

  const handleDelete = async (userId) => {
    try {
      await axios.delete(`${API}/admin/users/${userId}`);
      toast.success('Kullanıcı silindi');
      loadUsers();
    } catch (error) {
      toast.error(typeof error.response?.data?.detail === 'string' ? error.response.data.detail : (Array.isArray(error.response?.data?.detail) ? error.response.data.detail.map(e => e.msg).join(', ') : 'Hata oluştu'));
    }
  };

  const handleCloseDialog = () => {
    setIsOpen(false);
    setEditingUser(null);
    setSelectedEmployee(null);
    setEmployeeSearch('');
    setSelectedPanels([]);
    setFormData({ name: '', password: '', profession_id: '', position_id: '', branch_id: '', employee_id: '' });
  };

  const handleOpenChange = (open) => {
    if (open) {
      setIsOpen(true);
    } else {
      handleCloseDialog();
    }
  };

  // Filter employees based on search
  const filteredEmployees = employees.filter(emp => {
    if (!employeeSearch) return true;
    const search = employeeSearch.toLowerCase();
    return (
      (emp.name && emp.name.toLowerCase().includes(search)) ||
      (emp.tc_no && emp.tc_no.includes(search)) ||
      (emp.position && emp.position.toLowerCase().includes(search))
    );
  });

  // Check if employee already has a user account
  const getEmployeeUserStatus = (employeeId) => {
    return users.some(u => u.employee_id === employeeId);
  };

  // Panel Selection Component
  const PanelSelection = () => (
    <div className="space-y-3">
      <Label className="text-sm font-medium">Panel Erişimi</Label>
      <p className="text-xs text-gray-500">Kullanıcının erişebileceği panelleri seçin</p>
      <div className="grid grid-cols-2 gap-2">
        {AVAILABLE_PANELS.map(panel => {
          const Icon = panel.icon;
          const isSelected = selectedPanels.includes(panel.id);
          return (
            <div
              key={panel.id}
              onClick={() => handlePanelToggle(panel.id)}
              className={`flex items-center gap-3 p-3 rounded-lg border-2 cursor-pointer transition-all ${
                isSelected
                  ? 'border-teal-500 bg-teal-50'
                  : 'border-gray-200 hover:border-gray-300'
              }`}
              data-testid={`panel-${panel.id}`}
            >
              <Checkbox
                checked={isSelected}
                onCheckedChange={() => handlePanelToggle(panel.id)}
              />
              <Icon className={`h-5 w-5 ${isSelected ? 'text-teal-600' : 'text-gray-400'}`} />
              <span className={`text-sm font-medium ${isSelected ? 'text-teal-700' : 'text-gray-600'}`}>
                {panel.name}
              </span>
            </div>
          );
        })}
      </div>
    </div>
  );

  // Get panel badges for user display
  const getPanelBadges = (panelAccess) => {
    if (!panelAccess || panelAccess.length === 0) return null;
    return panelAccess.map(panelId => {
      const panel = AVAILABLE_PANELS.find(p => p.id === panelId);
      if (!panel) return null;
      return (
        <Badge key={panelId} variant="secondary" className={`${panel.color} text-xs`}>
          {panel.name}
        </Badge>
      );
    });
  };

  return (
    <div>
      <div className="flex justify-between items-center mb-6">
        <h2 className="text-2xl font-bold">Kullanıcı Yönetimi</h2>
        <Dialog open={isOpen} onOpenChange={handleOpenChange}>
          <DialogTrigger asChild>
            <Button data-testid="add-user-btn">
              <Plus className="h-4 w-4 mr-2" />
              Yeni Kullanıcı
            </Button>
          </DialogTrigger>
          <DialogContent className="max-w-lg max-h-[90vh] overflow-y-auto">
            <DialogHeader>
              <DialogTitle>{editingUser ? 'Kullanıcı Düzenle' : 'Yeni Kullanıcı Ekle'}</DialogTitle>
            </DialogHeader>
            
            {editingUser ? (
              /* Düzenleme Formu - Tabs yok */
              <form onSubmit={handleSubmit} className="space-y-4">
                <div>
                  <Label htmlFor="edit-name">Ad Soyad*</Label>
                  <Input
                    id="edit-name"
                    value={formData.name}
                    onChange={(e) => setFormData({...formData, name: e.target.value})}
                    placeholder="Ahmet Yılmaz"
                    required
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-password">Şifre (değiştirmek için doldurun)</Label>
                  <Input
                    id="edit-password"
                    type="password"
                    value={formData.password}
                    onChange={(e) => setFormData({...formData, password: e.target.value})}
                    placeholder="Boş bırakın (değiştirmek istemiyorsanız)"
                  />
                </div>
                
                <div>
                  <Label htmlFor="edit-profession">Meslek*</Label>
                  <Select value={formData.profession_id} onValueChange={(val) => setFormData({...formData, profession_id: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Meslek seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {professions.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="edit-position">Görev*</Label>
                  <Select value={formData.position_id} onValueChange={(val) => setFormData({...formData, position_id: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Görev seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {positions.map(p => (
                        <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                
                <div>
                  <Label htmlFor="edit-branch">Şube*</Label>
                  <Select value={formData.branch_id} onValueChange={(val) => setFormData({...formData, branch_id: val})}>
                    <SelectTrigger>
                      <SelectValue placeholder="Şube seçin" />
                    </SelectTrigger>
                    <SelectContent>
                      {branches.map(b => (
                        <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Panel Selection */}
                <PanelSelection />
                
                <Button type="submit" className="w-full bg-teal-600 hover:bg-teal-700" disabled={loading}>
                  {loading ? 'Güncelleniyor...' : 'Kullanıcı Güncelle'}
                </Button>
              </form>
            ) : (
            <Tabs defaultValue="list" className="w-full">
              <TabsList className="grid w-full grid-cols-2">
                <TabsTrigger value="list" data-testid="tab-list">
                  <Users className="h-4 w-4 mr-2" />
                  İK Listesinden Seç
                </TabsTrigger>
                <TabsTrigger value="manual" data-testid="tab-manual">Elle Oluştur</TabsTrigger>
              </TabsList>
              
              {/* İK Listesinden Seç Tab */}
              <TabsContent value="list" className="space-y-4">
                {/* Search */}
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400" />
                  <Input
                    placeholder="Çalışan ara (ad, TC no, pozisyon)"
                    value={employeeSearch}
                    onChange={(e) => setEmployeeSearch(e.target.value)}
                    className="pl-10"
                    data-testid="employee-search"
                  />
                </div>

                {/* Employee List */}
                <ScrollArea className="h-[200px] border rounded-md">
                  {employeesLoading ? (
                    <div className="flex items-center justify-center h-full">
                      <p className="text-gray-500">Çalışanlar yükleniyor...</p>
                    </div>
                  ) : filteredEmployees.length === 0 ? (
                    <div className="flex flex-col items-center justify-center h-full p-4">
                      <Users className="h-12 w-12 text-gray-300 mb-2" />
                      <p className="text-gray-500 text-center">
                        {employeeSearch ? 'Aramayla eşleşen çalışan bulunamadı' : 'İK sisteminde çalışan bulunamadı'}
                      </p>
                    </div>
                  ) : (
                    <div className="p-2 space-y-1">
                      {filteredEmployees.map(emp => {
                        const hasUser = getEmployeeUserStatus(emp.id);
                        const isSelected = selectedEmployee?.id === emp.id;
                        
                        return (
                          <div
                            key={emp.id}
                            onClick={() => !hasUser && handleSelectEmployee(emp)}
                            className={`p-3 rounded-md border cursor-pointer transition-all ${
                              hasUser 
                                ? 'bg-gray-100 border-gray-200 cursor-not-allowed opacity-60' 
                                : isSelected
                                ? 'bg-teal-50 border-teal-500 ring-2 ring-teal-500'
                                : 'hover:bg-gray-50 border-gray-200'
                            }`}
                            data-testid={`employee-item-${emp.id}`}
                          >
                            <div className="flex items-center justify-between">
                              <div className="flex-1">
                                <div className="flex items-center gap-2">
                                  <span className="font-medium">{emp.name}</span>
                                  {hasUser && (
                                    <span className="px-2 py-0.5 text-xs rounded bg-green-100 text-green-700">
                                      Kullanıcısı var
                                    </span>
                                  )}
                                </div>
                                <div className="text-sm text-gray-500">
                                  {emp.profession || emp.position_name || emp.position || 'Meslek/Görev belirtilmemiş'} • {emp.tc_no || '-'}
                                </div>
                              </div>
                              {isSelected && (
                                <Check className="h-5 w-5 text-teal-600" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </ScrollArea>

                {/* Selected Employee Info & Form */}
                {selectedEmployee && (
                  <div className="space-y-4 p-4 bg-teal-50 rounded-md border border-teal-200">
                    <div className="flex items-center gap-2">
                      <Check className="h-5 w-5 text-teal-600" />
                      <span className="font-medium text-teal-800">
                        Seçilen: {selectedEmployee.name}
                      </span>
                    </div>
                    
                    <div>
                      <Label htmlFor="emp-password">Şifre Belirle*</Label>
                      <Input
                        id="emp-password"
                        type="password"
                        value={formData.password}
                        onChange={(e) => setFormData({...formData, password: e.target.value})}
                        placeholder="Kullanıcı şifresi"
                        required
                        data-testid="employee-password"
                      />
                    </div>

                    <div className="grid grid-cols-2 gap-2">
                      <div>
                        <Label htmlFor="emp-profession">Meslek*</Label>
                        <Select 
                          value={formData.profession_id} 
                          onValueChange={(val) => setFormData({...formData, profession_id: val})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Meslek seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {professions.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                      <div>
                        <Label htmlFor="emp-position">Görev*</Label>
                        <Select 
                          value={formData.position_id} 
                          onValueChange={(val) => setFormData({...formData, position_id: val})}
                        >
                          <SelectTrigger>
                            <SelectValue placeholder="Görev seçin" />
                          </SelectTrigger>
                          <SelectContent>
                            {positions.map(p => (
                              <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                      </div>
                    </div>

                    <div>
                      <Label htmlFor="emp-branch">Şube*</Label>
                      <Select 
                        value={formData.branch_id} 
                        onValueChange={(val) => setFormData({...formData, branch_id: val})}
                      >
                        <SelectTrigger>
                          <SelectValue placeholder="Şube seçin" />
                        </SelectTrigger>
                        <SelectContent>
                          {branches.map(b => (
                            <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </div>

                    {/* Panel Selection */}
                    <PanelSelection />

                    <Button 
                      type="button" 
                      className="w-full bg-teal-600 hover:bg-teal-700" 
                      disabled={loading || !formData.password || !formData.profession_id || !formData.position_id || !formData.branch_id}
                      onClick={handleSubmit}
                      data-testid="create-user-from-employee"
                    >
                      {loading ? 'Oluşturuluyor...' : 'Kullanıcı Oluştur'}
                    </Button>
                  </div>
                )}
              </TabsContent>
              
              {/* Elle Oluştur Tab */}
              <TabsContent value="manual">
                <form onSubmit={handleSubmit} className="space-y-4">
                  <div>
                    <Label htmlFor="name">Ad Soyad*</Label>
                    <Input
                      id="name"
                      value={formData.name}
                      onChange={(e) => setFormData({...formData, name: e.target.value})}
                      placeholder="Ahmet Yılmaz"
                      required
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="password">Şifre{editingUser ? '' : '*'}</Label>
                    <Input
                      id="password"
                      type="password"
                      value={formData.password}
                      onChange={(e) => setFormData({...formData, password: e.target.value})}
                      required={!editingUser}
                      placeholder={editingUser ? 'Boş bırakın (değiştirmek istemiyorsanız)' : ''}
                    />
                  </div>
                  
                  <div>
                    <Label htmlFor="profession">Meslek*</Label>
                    <Select value={formData.profession_id} onValueChange={(val) => setFormData({...formData, profession_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Meslek seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {professions.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="position">Görev*</Label>
                    <Select value={formData.position_id} onValueChange={(val) => setFormData({...formData, position_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Görev seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {positions.map(p => (
                          <SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  
                  <div>
                    <Label htmlFor="branch">Şube*</Label>
                    <Select value={formData.branch_id} onValueChange={(val) => setFormData({...formData, branch_id: val})}>
                      <SelectTrigger>
                        <SelectValue placeholder="Şube seçin" />
                      </SelectTrigger>
                      <SelectContent>
                        {branches.map(b => (
                          <SelectItem key={b.id} value={b.id}>{b.name}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  {/* Panel Selection */}
                  <PanelSelection />
                  
                  <Button type="submit" className="w-full" disabled={loading}>
                    {loading ? (editingUser ? 'Güncelleniyor...' : 'Ekleniyor...') : (editingUser ? 'Kullanıcı Güncelle' : 'Kullanıcı Ekle')}
                  </Button>
                </form>
              </TabsContent>
            </Tabs>
            )}
          </DialogContent>
        </Dialog>
      </div>

      <Card>
        <CardContent className="p-0">
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Ad Soyad</TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('profession')}
                >
                  <div className="flex items-center">
                    Meslek
                    <SortIcon field="profession" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('position')}
                >
                  <div className="flex items-center">
                    Görev
                    <SortIcon field="position" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('branch')}
                >
                  <div className="flex items-center">
                    Şube
                    <SortIcon field="branch" />
                  </div>
                </TableHead>
                <TableHead 
                  className="cursor-pointer hover:bg-gray-100 select-none"
                  onClick={() => handleSort('panel_access')}
                >
                  <div className="flex items-center">
                    Panel Erişimi
                    <SortIcon field="panel_access" />
                  </div>
                </TableHead>
                <TableHead>İşlemler</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedUsers.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={6} className="text-center py-8 text-[hsl(var(--muted-foreground))]">
                    Henüz kullanıcı eklenmedi
                  </TableCell>
                </TableRow>
              ) : (
                sortedUsers.map(user => (
                  <TableRow key={user.id}>
                    <TableCell className="font-medium">{user.name}</TableCell>
                    <TableCell>{user.profession_name || '-'}</TableCell>
                    <TableCell>{user.position_name || '-'}</TableCell>
                    <TableCell>{user.branch_name || '-'}</TableCell>
                    <TableCell>
                      <div className="flex flex-wrap gap-1">
                        {getPanelBadges(user.panel_access) || (
                          <span className="text-gray-400 text-sm">-</span>
                        )}
                      </div>
                    </TableCell>
                    <TableCell>
                      <div className="flex gap-2">
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => handleEdit(user)}
                          data-testid={`edit-user-${user.id}`}
                        >
                          <Pencil className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button
                              variant="ghost"
                              size="sm"
                              className="text-[hsl(var(--destructive))]"
                              data-testid={`delete-user-${user.id}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Kullanıcıyı Sil</AlertDialogTitle>
                              <AlertDialogDescription>
                                "{user.name}" kullanıcısını silmek istediğinize emin misiniz? Bu işlem geri alınamaz.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>İptal</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => handleDelete(user.id)}
                                className="bg-[hsl(var(--destructive))] text-white hover:bg-red-600"
                              >
                                Sil
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </CardContent>
      </Card>
    </div>
  );
};

export default UserManagement;
